// CSS
import './DatabaseInstance.css';
// React hooks
import { useState, useEffect, act } from 'react';
import { useNavigate, useLocation, data } from 'react-router-dom';
// Skybase hooks
import { useData } from '../../../context/DataContext';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config';
// Skybase components
import InformationalPopover from '../../../components/Popovers/InformationalPopover/InformationalPopover';
// External hooks/components
import { useClerk, useUser, Show } from '@clerk/react';
import { useMediaQuery } from 'react-responsive';
// Media assets
import SearchIcon from '../../../assets/StandardIcons/search-icon-transparent.png';
import AddPinIcon from '../../../assets/StandardIcons/PinIcons/pin-icon.png';
import CheckmarkIcon from '../../../assets/StandardIcons/checkmark-icon.png';



// ================
// DatabaseInstance – represents a Skybase Engine.
// ================
function DatabaseInstance({
    databaseJson,           // JSON
}){
    // destructure global context
    const { activeEngineJson, changeActiveEngineId, pinnedEngineJson, fetchPinnedEngineJson, pinEngine, unpinEngine } = useData();
    const { isSubscribed, checkIsSubscribed } = useAuth();

    // init hooks
    const navigate = useNavigate('');
    const location = useLocation();
    const clerk = useClerk();
    const clerkUser = useUser();

    // vars
    const isLibraryPage = !location.pathname.includes('db');

    // responsive CSS
    const isMobile = useMediaQuery({ maxWidth: 650 });
    const isSmallScreen = useMediaQuery({ maxWidth: 400 });


    // ⚠️⚠️⚠️
    // ⚠️⚠️⚠️
    // ⚠️⚠️⚠️
    // TO-DO: Render TWO different Components for 'isDesktop' (normal) and for 'isMobile' (phone).
    // NOTE: The phone version will compact everything into a small screen.
    // ⚠️⚠️⚠️
    // ⚠️⚠️⚠️


    // ====================
    // FetchSourceOrgJson()
    // ====================
    const [sourceOrgJson, setSourceOrgJson] = useState(null);
    useEffect(() => {
        fetchSourceOrgJson();
    }, []);
    async function fetchSourceOrgJson(){
        if (!databaseJson) { return }; // null check

        // GET `/organizations/{id}`
        const response_object = await fetch(`${API_BASE_URL}/api/v1/organizations/${encodeURIComponent(databaseJson.source_organization)}`);
        if (!response_object.ok) { throw new Error(`SKYBASE (client): HTTP error ${response_object.status} encountered when fetching organization ${databaseJson.source_organization}`); }
        const source_org_json = await response_object.json();
        setSourceOrgJson(source_org_json);
    }


    // ===================
    // Track pinned status
    // ===================
    // isPinned
    const isPinned = pinnedEngineJson.some(engineJson => engineJson.id === databaseJson.id);
    // pin/unpin an Engine
    const onPinButtonClick = () => {
        
        // either PIN or UNPIN the Engine
        if (isPinned) {
            unpinEngine(databaseJson.id);
        } else {
            pinEngine(databaseJson.id);
        }

    }

    
    // =======================
    // activate button onClick
    // =======================
    // InformationPopover state
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    // Refresh isSubscribed status of CurrentUser
    useEffect(() => { checkIsSubscribed(); }, []);
    
    // onClick behavior
    const onActivateButtonClick = () => {
        
        // isSubscribed – redirect CurrentUser to "/search" page
        if (isSubscribed) {
            changeActiveEngineId(databaseJson.id);
            navigate('../search');
        }

        // !isSubscribed – open InformationalPopover
        if (!isSubscribed) {
            setIsInformationalPopoverOpen(true);
        }
    }

    
    // JSX Component
    return(
        <>
            {/* !isDesktop */}
            { !isMobile && 
                <div className={`DatabaseInstance ${databaseJson.id === activeEngineJson?.id ? 'active' : ''}`}>
                    {/* Name (Row) */}
                    <div className='DatabaseInstance-name-row'>{databaseJson?.name}</div>

                    {/* Source organization (Row) */}
                    <div className='DatabaseInstance-source-org-row'>{sourceOrgJson?.name}</div>

                    {/* Description (Row) */}
                    <div className='DatabaseInstance-description-row'>{databaseJson?.disclaimer}</div>
                    
                    {/* Metadata (Row) */}
                    <div className='DatabaseInstance-metadata-row'>
                        {/* Left block */}
                        <div className='DatabaseInstance-metadata-row-left-block'>ID: {databaseJson?.id}</div>

                        {/* Right block */}
                        <div className='DatabaseInstance-metadata-row-right-block'>

                            {/* IFF signed-IN */}
                            <Show when="signed-in">
                                {/* === ../db page ONLY === */}
                                { !isLibraryPage && <>

                                    {/* Pinned button (inactive) */}
                                    { !isPinned && <div className='DatabaseInstance-metadata-row-right-block-pinned-button' onClick={() => onPinButtonClick()}>
                                        <img src={AddPinIcon} className='DatabaseInstance-metadata-row-right-block-pinned-button--icon'/>Pin database
                                    </div>}

                                    {/* Pinned button (active) */}
                                    { isPinned && <div className='DatabaseInstance-metadata-row-right-block-pinned-button--active' onClick={() => onPinButtonClick()}>
                                        <img src={CheckmarkIcon} className='DatabaseInstance-metadata-row-right-block-pinned-button--checkmark-icon'/>Pinned
                                    </div>}

                                    {/* Active button (already activated) */}
                                    { databaseJson?.id === activeEngineJson?.id &&
                                        <div className='DatabaseInstance-metadata-row-right-block-activate-button--active'>
                                            <img src={CheckmarkIcon} className='DatabaseInstance-metadata-row-right-block-pinned-button--checkmark-icon'/>Selected
                                        </div>
                                    }

                                    {/* Activate button  (not yet activated) */}
                                    { (databaseJson?.id !== activeEngineJson?.id) && 
                                        <div className='DatabaseInstance-metadata-row-right-block-activate-button' onClick={() => onActivateButtonClick()}>
                                            <img src={SearchIcon} className='DatabaseInstance-metadata-row-right-block-activate-button--search-icon'/>Search Now
                                        </div>
                                    }
                                </>}
                                

                                {/* === ../library page ONLY === */}
                                {/* Activate button */}
                                { isLibraryPage && <>
                                    <div className='DatabaseInstance-metadata-row-right-block-activate-button' onClick={() => onActivateButtonClick()}>
                                        <img src={SearchIcon} className='DatabaseInstance-metadata-row-right-block-activate-button--search-icon'/>Search Now
                                    </div>
                                </>}

                            </Show>

                            {/* IFF signed-OUT */}
                            <Show when="signed-out">
                                <div className='DatabaseInstance-metadata-row-right-block-activate-button' onClick={() => navigate('/create-account', {state: { from: location.pathname }}) }>
                                    <img src={SearchIcon} className='DatabaseInstance-metadata-row-right-block-activate-button--search-icon' alt="Search icon."/>Search Now
                                </div>
                            </Show>

                        </div>
                    </div>

                    
                </div>
            }

            {/* isMobile */}
            { isMobile && 
                <div className={`DatabaseInstance-mobile ${databaseJson.id === activeEngineJson?.id ? 'active' : ''}`}>
                    
                    {/* Name (Row) */}
                    <div className='DatabaseInstance-mobile-name-row'>{databaseJson?.name}</div>

                    {/* Source organization (Row) */}
                    <div className='DatabaseInstance-mobile-source-org-row'>{sourceOrgJson?.name}</div>

                    {/* ID (row) */}
                    <div className='DatabaseInstance-mobile-id-row'>ID: {databaseJson?.id}</div>

                    {/* Buttons (row) */}
                    <div className='DatabaseInstance-mobile-buttons-row'>
                        
                        {/* CONDITION: isSignedIn */}
                        <Show when="signed-in">

                            {/* CONDITION: !isLibraryPage */}
                            { !isLibraryPage && 
                                <>
                                    {/* Pin button */}
                                    { !isPinned &&
                                        <div className='DatabaseInstance-mobile-buttons-row-pin-button' onClick={() => onPinButtonClick()}>
                                            <img src={AddPinIcon} className='DatabaseInstance-metadata-row-right-block-pinned-button--icon'/>Pin
                                        </div>
                                    }

                                    {/* Unpin button */}
                                    { isPinned &&
                                        <div className='DatabaseInstance-mobile-buttons-row-pin-button--active' onClick={() => onPinButtonClick()}>
                                            <img src={CheckmarkIcon} className='DatabaseInstance-metadata-row-right-block-pinned-button--checkmark-icon'/>Pinned
                                        </div>
                                    }
                                </>
                            }

                            {/* Search button */}
                            <div className='DatabaseInstance-mobile-buttons-row-search-button' onClick={() => onActivateButtonClick()}>
                                <img src={SearchIcon} className='DatabaseInstance-metadata-row-right-block-activate-button--search-icon'/>Search Now
                            </div>

                        </Show>


                        {/* CONDITION: !isSignedIn */}
                        <Show when="signed-out">

                            {/* Search button */}
                            <div className='DatabaseInstance-mobile-buttons-row-search-button' onClick={() => navigate('/create-account', {state: { from: location.pathname }})}>
                                <img src={SearchIcon} className='DatabaseInstance-metadata-row-right-block-activate-button--search-icon'/>Search Now
                            </div>

                        </Show>

                    </div>

                </div>
            }

            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Subscription Required"}
                    bodyText={"Please purchase a Skybase subscription plan to continue."}
                    primaryButtonText={"View Subscription Plans"}
                    primaryButtonCallback={() => navigate('../pricing')}
                    secondaryButtonText={"Close"}
                    secondaryButtonCallback={() => setIsInformationalPopoverOpen(false)}
                />
            }

        </>
    );
};


// Export
export default DatabaseInstance;